import axios from 'axios';
import { Capacitor } from '@capacitor/core';

export const getApiBase = (): string => {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (Capacitor.isNativePlatform()) {
    return 'https://gym-rag.whfc.cc/api';
  }
  return '/api';
};
export const API_BASE = getApiBase();

// 图片代理 - 使用 wsrv.nl (weserv) 图片代理服务，浏览器直连
const IMAGE_PROXY = 'https://wsrv.nl/?url=';

export const proxyImage = (url: string, width?: number, height?: number): string => {
  if (!url) return '/default-avatar.png';
  const httpsUrl = url.replace('http://', 'https://');
  let result = IMAGE_PROXY + encodeURIComponent(httpsUrl);
  // 按 2x 分辨率请求，适配高分屏 (DPR)
  if (width && height) {
    result += `&w=${width * 2}&h=${height * 2}`;
  }
  return result;
};

// ===== Axios interceptor for auth =====

axios.interceptors.request.use(config => {
  const token = localStorage.getItem('gym_rag_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  res => res,
  err => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('gym_rag_token');
    }
    return Promise.reject(err);
  }
);

// ===== Types =====

export interface Document {
  id: number;
  title: string;
  year: number;
  source_url: string;
  created_at: string;
  content_length: number;
  content?: string;
}

export interface ChatMessage {
  id: number;
  question: string;
  answer: string;
  sources: Array<{ title: string; year: number }>;
  created_at: string;
}

export interface Conversation {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  message_count?: number;
  pinned?: number;
}

export interface Coach {
  id: number;
  name: string;
  avatar: string;
  bio: string;
  upmaster_ids: number[];
  persona_prompt?: string;
  video_count?: number;
}

export interface ConversationMessage {
  id: number;
  question: string;
  answer: string;
  sources: StreamSource[];
  created_at: string;
  tool_calls?: ToolCallSummary[];
}

export interface ToolCallSummary {
  name: string;
  args: Record<string, any>;
  result?: string | null;
}

export interface StreamSource {
  title: string;
  url: string;
  bvid?: string;
  timestamp?: string;
  start_time?: number;
  upmaster_name?: string;
  content?: string;
  pic?: string;
}

export type ChatStreamEvent =
  | { type: 'conversation'; conversation_id: number; created?: boolean }
  | { type: 'sources'; sources: StreamSource[] }
  | { type: 'tool_calls'; calls: Array<{ name: string; args: Record<string, any> }> }
  | { type: 'tool_results'; results: Array<{ name: string; result: any }> }
  | { type: 'token'; text: string }
  | { type: 'status'; message: string }
  | { type: 'error'; message: string }
  | { type: 'done'; full_answer: string; chat_id?: number | null };

export type StreamCallback = (event: ChatStreamEvent) => void;

// ===== API =====

const api = {
  // ===== Auth =====
  login: (username: string, password: string) =>
    axios.post(`${API_BASE}/auth/login`, { username, password }),

  register: (username: string, password: string) =>
    axios.post(`${API_BASE}/auth/register`, { username, password }),

  getMe: () => axios.get(`${API_BASE}/auth/me`),

  // ===== 电子教练（v21）=====
  getCoaches: () => axios.get(`${API_BASE}/coaches`),
  getCoach: (coachId: number) => axios.get(`${API_BASE}/coaches/${coachId}`),
  getCoachConversation: (coachId: number) =>
    axios.get(`${API_BASE}/coaches/${coachId}/conversation`),
  resetCoachConversation: (coachId: number) =>
    axios.post(`${API_BASE}/coaches/${coachId}/conversation/reset`),

  // ===== Up 主管理 =====
  getUpMasters: () => axios.get(`${API_BASE}/upmasters`),
  addUpMaster: (urlOrUid: string, platform: string = 'bilibili') =>
    axios.post(`${API_BASE}/upmasters`, { url_or_uid: urlOrUid, platform }),
  deleteUpMaster: (id: number) => axios.delete(`${API_BASE}/upmasters/${id}`),
  searchUpmasters: (q: string) =>
    axios.get(`${API_BASE}/upmasters/search`, { params: { q } }),

  // ===== 视频列表 =====
  getVideos: (upmasterId: number, page: number = 1, pageSize: number = 20) =>
    axios.get(`${API_BASE}/upmasters/${upmasterId}/videos`, {
      params: { page, page_size: pageSize },
    }),

  // ===== 爬取 =====
  startCrawl: (upmasterId: number) => axios.post(`${API_BASE}/upmasters/${upmasterId}/crawl`),

  // ===== 加载知识库 =====
  loadKnowledgeBase: (upmasterId: number) => axios.post(`${API_BASE}/upmasters/${upmasterId}/load`),

  // ===== 问答 =====
  chat: (upmasterId: number, question: string, topK: number = 5, upmasterIds?: number[], conversationId?: number, coachId?: number) => {
    const body: any = { upmaster_id: upmasterId, question, top_k: topK };
    if (upmasterIds) body.upmaster_ids = upmasterIds;  // 多源过滤；空/缺省 → 全部
    if (conversationId) body.conversation_id = conversationId;
    if (coachId) body.coach_id = coachId;  // 电子教练模式：强制知识隔离 + 唯一会话
    return axios.post(`${API_BASE}/chat`, body);
  },

  // ===== 流式问答 (SSE via fetch) =====
  chatStream: async (
    upmasterId: number,
    question: string,
    onEvent: StreamCallback,
    topK: number = 5,
    signal?: AbortSignal,
    upmasterIds?: number[],
    conversationId?: number,
    coachId?: number,
  ): Promise<void> => {
    const token = localStorage.getItem('gym_rag_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const body: any = { upmaster_id: upmasterId, question, top_k: topK };
    if (upmasterIds) body.upmaster_ids = upmasterIds;  // 多源过滤；空/缺省 → 全部
    if (conversationId) body.conversation_id = conversationId;
    if (coachId) body.coach_id = coachId;  // 电子教练模式：强制知识隔离 + 唯一会话

    const response = await fetch(`${API_BASE}/chat/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          const trimmed = part.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          try {
            const event: ChatStreamEvent = JSON.parse(trimmed.slice(6));
            onEvent(event);
          } catch {
            // skip malformed events
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  // ===== 问答历史 =====
  getChatHistory: (upmasterId: number, limit: number = 20) =>
    axios.get(`${API_BASE}/upmasters/${upmasterId}/chat-history?limit=${limit}`),

  deleteChatHistory: (chatId: number) =>
    axios.delete(`${API_BASE}/chat-history/${chatId}`),

  // ===== 回答反馈 =====
  submitFeedback: (chatId: number, rating: number, reason?: string) =>
    axios.post(`${API_BASE}/feedback`, { chat_id: chatId, rating, reason }),

  // ===== 会话（conversations）=====
  getConversations: () =>
    axios.get(`${API_BASE}/conversations`),
  createConversation: (title?: string) =>
    axios.post(`${API_BASE}/conversations`, title ? { title } : {}),
  getConversationMessages: (conversationId: number) =>
    axios.get(`${API_BASE}/conversations/${conversationId}/messages`),
  deleteConversation: (conversationId: number) =>
    axios.delete(`${API_BASE}/conversations/${conversationId}`),
  pinConversation: (conversationId: number) =>
    axios.post(`${API_BASE}/conversations/${conversationId}/pin`),
  unpinConversation: (conversationId: number) =>
    axios.post(`${API_BASE}/conversations/${conversationId}/unpin`),

  // ===== 健康检查 =====
  healthCheck: () => axios.get(`${API_BASE}/health`),

  // ===== 文档管理 =====
  getDocuments: () => axios.get<{ success: boolean; data: Document[] }>(`${API_BASE}/documents`),
  getDocument: (id: number) => axios.get<{ success: boolean; data: Document }>(`${API_BASE}/documents/${id}`),
  crawlDocuments: (startYear?: number, endYear?: number) =>
    axios.post(`${API_BASE}/documents/crawl`, null, { params: { start_year: startYear, end_year: endYear } }),
  loadDocuments: () => axios.post<{ success: boolean; data: { doc_count: number } }>(`${API_BASE}/documents/load`),
  deleteDocument: (id: number) => axios.delete(`${API_BASE}/documents/${id}`),
  chatWithDocuments: (question: string, topK: number = 5) =>
    axios.post(`${API_BASE}/documents/chat`, null, { params: { question, top_k: topK } }),

  // ===== 数据源申请与订阅 =====
  getTools: () => axios.get(`${API_BASE}/tools`),
  parseDataSource: (url: string) =>
    axios.post(`${API_BASE}/data-sources/parse`, { url }),
  createDataSourceRequest: (data: { name: string; url: string; reason: string }) =>
    axios.post(`${API_BASE}/data-sources/requests`, data),
  getDataSourceRequests: (scope: string = 'mine', status?: string) =>
    axios.get(`${API_BASE}/data-sources/requests`, { params: { scope, status } }),
  getMyDataSources: () => axios.get(`${API_BASE}/users/me/data-sources`),
  updateMyDataSources: (upmasterIds: number[]) =>
    axios.put(`${API_BASE}/users/me/data-sources`, { upmaster_ids: upmasterIds }),

  // ===== Skill 知识卡片 =====
  getUpmasterSkill: (upmasterId: number) =>
    axios.get(`${API_BASE}/upmasters/${upmasterId}/skill`),
  downloadSkill: (upmasterId: number) =>
    axios.get(`${API_BASE}/upmasters/${upmasterId}/skill/download`, {
      responseType: 'blob',
    }),
  getSkillInstallText: (upmasterId: number) =>
    axios.get(`${API_BASE}/upmasters/${upmasterId}/skill/install-text`, {
      responseType: 'text',
    }),

  // ===== 训练记录 =====
  getCalendar: (year: number, month: number) =>
    axios.get(`${API_BASE}/training/calendar`, { params: { year, month } }),

  getTrainingDay: (date: string) =>
    axios.get(`${API_BASE}/training/days/${date}`),

  saveTrainingDay: (date: string, exercises: Array<{ name: string; sets: Array<{ weight_kg: number; reps: number }> }>) =>
    axios.put(`${API_BASE}/training/days/${date}`, { exercises }),

  deleteTrainingDay: (date: string) =>
    axios.delete(`${API_BASE}/training/days/${date}`),

  // ===== 训练计划 =====
  getPlans: () => axios.get(`${API_BASE}/training/plans`),
  createPlanByLLM: (data: {
    name?: string;
    goal: string;
    days_per_week: number;
    equipment?: string[];
    experience_level?: string;
    notes?: string;
  }) => axios.post(`${API_BASE}/training/plans/generate`, data),
  createPlan: (data: { name: string; description?: string; plan_data: any; is_active?: boolean }) =>
    axios.post(`${API_BASE}/training/plans`, data),
  updatePlan: (id: number, data: { name?: string; description?: string; plan_data?: any; is_active?: boolean }) =>
    axios.put(`${API_BASE}/training/plans/${id}`, data),
  deletePlan: (id: number) => axios.delete(`${API_BASE}/training/plans/${id}`),
};

export default api;
